import { CrossSellChannel, ILogger, ITopicEnvelope, MessageBroker } from '@sparkware/uc-sdk-core';
import Container, { Service } from 'typedi';
import { LoggerProvider } from '../logger';
import { IClientProductParams } from './models';
import { WindowToken } from '../../injection-tokens';
import { LoaderManager } from '../../loaders/LoaderManager';
import { ICRM } from '../crm/models';
import ClientProductPackageApi, {
    IOpenClientProductPackageDataRequest,
} from '../../../APIs/ClientProductPackageApi';
import { CloseSessionReason } from '../../models/enums/general-enums';
import { ClientTracking } from '../tracking/models/clientTracking';
import { GNWPElasticEvents } from './enums';
import { GNWPUtils } from './gnwp-utils';
import { ILogout } from '../logout/models';

@Service()
export class ClientProductService {
    private readonly _logger: ILogger;
    private readonly _window: Window;
    private readonly _clientTracking: ClientTracking;
    private readonly _clientTrackingEnvelop: ITopicEnvelope;
    private readonly _gnwpUtils: GNWPUtils;
    private readonly _crossSellChannel: CrossSellChannel;

    private get _crmPromise(): Promise<ICRM> {
        return LoaderManager.Instance.CRMLoader.Instance;
    }
    private get _logoutPromise(): Promise<ILogout> {
        return LoaderManager.Instance.LogoutLoader.Instance;
    }

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('clientProductPackage');

        this._window = Container.get(WindowToken);
        this._clientTracking = Container.get(ClientTracking);
        this._gnwpUtils = Container.get(GNWPUtils);

        this._crossSellChannel = MessageBroker.getInstance().crossSell;

        this._clientTrackingEnvelop = {
            headers: {
                publisher: 'clientProductPackage',
            },
            topic: null,
        };
    }

    public async openClientAuthenticated(params: IClientProductParams): Promise<void> {
        const data: IOpenClientProductPackageDataRequest = {
            ProductOfferingId: params.productOfferingId,
            SubBrandId: params.subBrandId,
            ProductPackageId: params.productPackageId,
            IsRealMode: params.isRealMode ? 1 : 0,
            AdditionalActivationParameters: params.additionalActivationParameters,
        };
        this._clientTracking.onSendEvent(
            {
                event: GNWPElasticEvents.OpenClientAuthenticated,
                dataString: JSON.stringify(params),
            },
            this._clientTrackingEnvelop,
        );

        const result = await ClientProductPackageApi.GetClientProductPackageDataAuthenticated(data);
        await this.handleOpenClientProductPackageResult(result.response, params, true, async () => {
            await (await this._logoutPromise)?.doLogout(CloseSessionReason.ClientDisconnected);
        });
    }

    public async openClientUnauthorized(params: IClientProductParams): Promise<void> {
        const data: IOpenClientProductPackageDataRequest = {
            ProductOfferingId: params.productOfferingId,
            SubBrandId: params.subBrandId,
            ProductPackageId: params.productPackageId,
            IsRealMode: params.isRealMode ? 1 : 0,
            AdditionalActivationParameters: params.additionalActivationParameters,
        };
        this._clientTracking.onSendEvent(
            {
                event: GNWPElasticEvents.OpenClientUnauthorized,
                dataString: JSON.stringify(params),
            },
            this._clientTrackingEnvelop,
        );

        const result = await ClientProductPackageApi.GetClientProductPackageDataUnauthorized(data);
        await this.handleOpenClientProductPackageResult(result.response, params, false);
    }

    private async handleOpenClientProductPackageResult(
        result: any,
        params: IClientProductParams,
        isAutoLogin: boolean,
        logoutCallback?: () => void,
    ) {
        if (result.isOk) {
            const deviceData = this._window.pageContextManager.getDeviceData();
            let paramsObj: Record<string, string> = {
                UserInfo: String(result.UserInfo),
                BrandId: String(result.BrandId),
                SourceSubBrand: String(result.SourceSubBrandID),
                TargetSubBrand: String(result.TargetSubBrand),
                SourceProductPackage: String(deviceData.productPackage),
                TargetProductPackage: String(result.TargetProductPackage),
                IsRealMode: String(result.IsRealMode),
                SpecificNavigation: String(result.SpecificNavigation),
                EncryptionType: String(result.EncryptionType),
                CurrencyCode: String(result.CurrencyCode),
                LangID: String(result.LangID),
                SessionTimer: String(result.SessionTimer),
                Domain: String(result.Domain),
            };
            if (params.linkId != null) {
                paramsObj.LinkID = String(params.linkId);
            }
            if (params.addToQueryString != null) {
                for (const key in params.addToQueryString) {
                    if (Object.prototype.hasOwnProperty.call(params.addToQueryString, key)) {
                        paramsObj[key] = JSON.stringify(params.addToQueryString[key]);
                    }
                }
            }
            if (params.target === 'open-cis') {
                const queryString = new URLSearchParams(paramsObj).toString();
                const url = new URL(result.GnwpUrl);
                url.search = queryString;
                const formattedUrl = url.toString();
                const resultData = {
                    url: formattedUrl,
                    relativeUrl: formattedUrl.replace(result.GnwpUrl, ''),
                    queryObject: paramsObj,
                };
                this._crossSellChannel.topics.CTA_CasinoClient_GetURL.publish(
                    { publisher: 'clientProductPackage' },
                    resultData,
                );
            } else if (params.target === 'casinoNativeIOS') {
                let casinoNativeUrl =
                    this._window.pageContextManager.getUrlResourceData().iosNativeCasinoUrl;
                let separator = '?';
                for (const key in paramsObj) {
                    casinoNativeUrl += `${separator}${key}=${encodeURIComponent(paramsObj[key])}`;
                    separator = '&';
                }
                (await this._crmPromise)?.onExternalLinkOpen({ Url: casinoNativeUrl });
            } else if (params.target != null && params.checkIfTargetStillExists != null) {
                if (params.checkIfTargetStillExists) {
                    // Convert paramsObj to array of { key, value } for addFormToBody
                    const paramsArr = Object.entries(paramsObj).map(([key, value]) => ({
                        key,
                        value,
                    }));
                    this._gnwpUtils.addFormToBody(
                        result.GnwpUrl,
                        paramsObj,
                        'post',
                        params.target,
                        params.windowReference,
                    );
                }
            } else if (params.target === 'native') {
                let url = `${result.GnwpUrl}?NativeMode=true`;
                for (const key in paramsObj) {
                    url += `&${key}=${encodeURIComponent(paramsObj[key])}`;
                }
                (await this._crmPromise)?.onExternalLinkOpen({ Url: url });
            } else {
                this._gnwpUtils.addFormToBody(
                    result.GnwpUrl,
                    paramsObj,
                    'post',
                    params.target,
                    params.windowReference,
                );
            }
        } else {
            if (isAutoLogin && (result.ErrorCode === 5 || result.ErrorCode === 6)) {
                this._logger.debug('GetToken failed, performing logout');
                if (logoutCallback) await logoutCallback();
            }
            this._logger.debug('Error in call to OpenClientProductPackage');
        }
    }
}
